import React, { useCallback, useState, DragEvent } from 'react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore, useFilteredAssets, useChildFolders } from '../../store/projectPanelStore';
import { AssetItem } from './AssetItem';
import { FolderGridItem } from './FolderGridItem';
import { processDroppedItems } from '../../utils/file-import-helpers';

export const AssetGrid: React.FC = () => {
  const {
    selectedAssetId,
    selectedAssetIds,
    selectAsset,
    gridSize,
    selectedFolderId,
    importExternalFiles,
    importExternalEntries,
  } = useProjectPanelStore();
  const assets = useFilteredAssets();
  const childFolders = useChildFolders();
  const [isDragOver, setIsDragOver] = useState(false);

  const gridClass = {
    small: 'grid-cols-[repeat(auto-fill,minmax(80px,1fr))]',
    medium: 'grid-cols-[repeat(auto-fill,minmax(100px,1fr))]',
    large: 'grid-cols-[repeat(auto-fill,minmax(140px,1fr))]',
  }[gridSize];

  // External file drag handlers
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    // Internal asset drag (AssetItem sets 'text/plain') — suppress import overlay
    if (e.dataTransfer.types.includes('text/plain')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    // External file drop — show import overlay
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    // Only clear if leaving the container entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    // Only handle external file drops, not internal asset drags
    if (e.dataTransfer.types.includes('text/plain') || !e.dataTransfer.types.includes('Files')) return;

    // Check if items API is available (for folder support via webkitGetAsEntry)
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      // Check if any item might be a directory
      const hasDirectoryOrMultiple = Array.from(e.dataTransfer.items).some(
        item => item.webkitGetAsEntry?.()?.isDirectory
      );

      if (hasDirectoryOrMultiple || e.dataTransfer.items.length > 0) {
        try {
          // Use the new method that supports folders
          const entries = await processDroppedItems(e.dataTransfer.items);
          if (entries.length > 0) {
            importExternalEntries(entries, selectedFolderId);
            return;
          }
        } catch (err) {
          console.warn('[AssetGrid] Error processing dropped items with webkitGetAsEntry, falling back:', err);
        }
      }
    }

    // Fallback to regular file handling
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      importExternalFiles(files, selectedFolderId);
    }
  }, [selectedFolderId, importExternalFiles, importExternalEntries]);

  const hasContent = childFolders.length > 0 || assets.length > 0;

  if (!hasContent) {
    return (
      <div
        className={cn(
          'flex-1 flex items-center justify-center text-zinc-500 text-sm transition-colors',
          isDragOver && 'bg-blue-900/30 outline outline-2 outline-blue-500 outline-dashed'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center pointer-events-none">
          <div className="text-4xl mb-2">{isDragOver ? '📥' : '📂'}</div>
          <div>{isDragOver ? 'Drop files or folders to import' : 'Empty folder'}</div>
          <div className="text-xs mt-1">Drag files or folders here, or use Import</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto p-2 grid gap-2 content-start transition-colors',
        gridClass,
        isDragOver && 'bg-blue-900/20 outline outline-2 outline-blue-500 outline-dashed'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="bg-blue-600/90 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="text-center">
              <div className="text-2xl mb-1">📥</div>
              <div className="text-sm font-medium">Drop files or folders to import</div>
            </div>
          </div>
        </div>
      )}
      {childFolders.map((folder) => (
        <FolderGridItem
          key={folder.id}
          folder={folder}
          size={gridSize}
        />
      ))}
      {assets.map((asset) => (
        <AssetItem
          key={asset.id}
          asset={asset}
          isSelected={selectedAssetId === asset.id || selectedAssetIds.includes(asset.id)}
          onSelect={selectAsset}
          size={gridSize}
        />
      ))}
    </div>
  );
};
